import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Card, CardActionArea, CardContent, Chip, Fab, Skeleton,
  useTheme, TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel,
  IconButton, Button, useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, FilterList as FilterIcon, Book as BookIcon,
  Clear as ClearIcon, CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useLanguage } from '../context/LanguageContext';
import JournalEditor from '../components/JournalEditor';
import JournalEntryView from '../components/JournalEntryView';
import { moodLabel } from '../components/journalMoods';
import apiClient from '../services/apiClient';

const JournalPage = () => {
  const { selectedLanguage, t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const location = useLocation();
  const navigate = useNavigate();

  const [view, setView] = useState('list');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  // Prefill text handed in by the floating tutor's "Add to journal" action.
  const [prefillContent, setPrefillContent] = useState(null);

  // If we arrived with prefill content (from the tutor), open the editor in
  // 'create' mode with that draft, then clear the router state so a refresh or
  // back-navigation doesn't re-trigger it.
  useEffect(() => {
    if (location.state?.prefillContent) {
      setPrefillContent(location.state.prefillContent);
      setView('create');
      navigate('.', { replace: true, state: null });
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const [searchQuery, setSearchQuery] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');

  useEffect(() => {
    if (selectedLanguage) fetchEntries();
  }, [selectedLanguage, filterMonth, filterYear, selectedTag]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const params = {
        target_language: selectedLanguage,
        search: searchQuery,
        month: filterMonth,
        year: filterYear,
        tag: selectedTag
      };
      const response = await apiClient.journal.getEntries(params);
      const fetchedEntries = response.data.entries || [];
      setEntries(fetchedEntries);

      const tags = new Set();
      fetchedEntries.forEach(e => e.tags?.forEach(t => tags.add(t)));
      setAvailableTags(Array.from(tags));
    } catch (error) {
      console.error("Error fetching journals:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (entryData) => {
    try {
      if (view === 'edit' && selectedEntry?.entry_id) {
        // Update in place (this used to POST, creating a duplicate entry).
        const response = await apiClient.journal.updateEntry(selectedEntry.entry_id, entryData);
        setSelectedEntry(response.data.entry);
        setView('view');
      } else {
        await apiClient.journal.saveEntry({ ...entryData, target_language: selectedLanguage });
        setView('list');
        setSelectedEntry(null);
      }
      fetchEntries();
    } catch (error) {
      console.error("Error saving journal:", error);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilterMonth('');
    setSelectedTag('');
  };

  const hasActiveFilters = searchQuery || filterMonth || selectedTag;

  // Show editor view (full screen)
  if (view === 'create') {
    return (
      <JournalEditor
        targetLanguage={selectedLanguage}
        initialContent={prefillContent}
        onSave={handleSave}
        onCancel={() => { setPrefillContent(null); setView('list'); }}
      />
    );
  }

  // Show edit view (full screen)
  if (view === 'edit' && selectedEntry) {
    return (
      <JournalEditor
        targetLanguage={selectedEntry.target_language || selectedLanguage}
        initialData={selectedEntry}
        onSave={handleSave}
        onCancel={() => setView('view')}
      />
    );
  }

  // Show entry detail view
  if (view === 'view' && selectedEntry) {
    return (
      <Box sx={{ pb: isMobile ? 8 : 4 }}>
        <JournalEntryView
          entry={selectedEntry}
          onBack={() => {
            setSelectedEntry(null);
            setView('list');
          }}
          onEdit={() => setView('edit')}
        />
      </Box>
    );
  }

  // List view
  return (
    <Box sx={{ pb: isMobile ? 10 : 4, minHeight: '80vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t?.('journalTitle') || 'Journal'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Practice writing in {selectedLanguage}
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: 2 }}>
          <form onSubmit={(e) => { e.preventDefault(); fetchEntries(); }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search entries..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchQuery('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Month</InputLabel>
                  <Select
                    value={filterMonth}
                    label="Month"
                    onChange={(e) => setFilterMonth(e.target.value)}
                  >
                    <MenuItem value="">All Time</MenuItem>
                    {Array.from({ length: 12 }, (_, i) => (
                      <MenuItem key={i + 1} value={i + 1}>
                        {new Date(0, i).toLocaleString('default', { month: 'long' })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tag</InputLabel>
                  <Select
                    value={selectedTag}
                    label="Tag"
                    onChange={(e) => setSelectedTag(e.target.value)}
                  >
                    <MenuItem value="">All Tags</MenuItem>
                    {availableTags.map(tag => (
                      <MenuItem key={tag} value={tag}>{tag}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {hasActiveFilters && (
                <Grid item xs={12} md={1}>
                  <Button
                    size="small"
                    onClick={handleClearFilters}
                    startIcon={<ClearIcon />}
                  >
                    Clear
                  </Button>
                </Grid>
              )}
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Loading state */}
      {loading && (
        <Grid container spacing={2}>
          {[1, 2, 3].map(i => (
            <Grid item xs={12} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Empty state */}
      {!loading && entries.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <BookIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {hasActiveFilters ? 'No entries found' : 'Start your journal'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {hasActiveFilters
              ? 'Try adjusting your filters'
              : 'Write your first entry to practice your language skills'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setView('create')}
          >
            {t?.('newEntry') || 'New Entry'}
          </Button>
        </Paper>
      )}

      {/* Entry list */}
      {!loading && entries.length > 0 && (
        <Grid container spacing={2}>
          {entries.map((entry) => (
            <Grid item xs={12} key={entry.entry_id}>
              <Card
                sx={{
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[4]
                  }
                }}
              >
                <CardActionArea onClick={() => { setSelectedEntry(entry); setView('view'); }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" noWrap sx={{ flex: 1, mr: 2 }}>
                        {entry.title || "Untitled"}
                      </Typography>
                      <Chip
                        icon={<CalendarIcon sx={{ fontSize: '0.9rem !important' }} />}
                        label={new Date(entry.created_at).toLocaleDateString()}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.5
                      }}
                    >
                      {entry.polished_content || entry.raw_content}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {entry.mood && (
                          <Chip
                            label={moodLabel(entry.mood)}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        )}
                        {entry.tags?.slice(0, 2).map(tag => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            sx={{ bgcolor: 'action.selected' }}
                          />
                        ))}
                        {entry.tags?.length > 2 && (
                          <Chip
                            label={`+${entry.tags.length - 2}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      {entry.glossary && entry.glossary.length > 0 && (
                        <Typography variant="caption" color="secondary" fontWeight="medium">
                          ✨ {entry.glossary.length} words learned
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* FAB for new entry - positioned above bottom nav and tutor */}
      <Fab
        color="primary"
        aria-label="add"
        onClick={() => setView('create')}
        sx={{
          position: 'fixed',
          bottom: isMobile ? 140 : 24,
          right: 24,
          zIndex: 1000
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default JournalPage;